import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFormContext } from '../contexts/FormContext';
import { duplicateForm } from '../utils/duplicateForm';
import { useAuth } from '../contexts/AuthContext';
import { useLoading } from '../contexts/LoadingContext';
import { safeGetItem } from '../utils/storageUtils';

export const useDuplicateForm = () => {
  const { currentUser, organizationId, hasPermission } = useAuth();
  const { setLoading } = useLoading();
  const navigate = useNavigate();
  const {
    setFormId,
    setFormTitle,        // ← comes from FormContext (alias of updateTitle)
    setFormDescription,  // ← alias of updateDescription
    setFormDetails,
    initializeNewFormLocally,
    formIdReady,
    formId: contextFormId,
    formTitle,
    formDescription,
    formDetails,
  } = useFormContext();

  const [duplicateModalState, setDuplicateModalState] = useState({
    open: false,
    formId: null,
    message: '',
  });

  const [error, setError] = useState(null);

  /* -------------------------------------------------------------------- *
   * 1️⃣  Trigger duplication (called from HomeCard or wherever)           *
   * -------------------------------------------------------------------- */
  const handleDuplicateForm = useCallback(
    async (sourceFormId) => {
    /* Cache the template’s current metadata so we can put it back
       after we temporarily switch the context ID below. */
      const originalDetails     = { ...formDetails };
      const originalTitle       = formTitle;
      const originalDescription = formDescription;


      if (!sourceFormId || !currentUser?.uid || !organizationId) {
        setError('Unable to duplicate — missing user or organization context.');
        return;
      }

      if (!hasPermission('createForms')) {
        setError('You do not have permission to duplicate forms.');
        return;
      }

      setLoading(true, 'duplicateForm');
      setError(null);

      try {
        const result = await duplicateForm(
          sourceFormId,
          { currentUser, organizationId },
          initializeNewFormLocally
        );

        if (result.status === 'success') {
          console.log('[useDuplicateForm] ✅ Duplication result:', result);
          //setFormId(result.newFormId);
          setFormId(sourceFormId);

          /* Roll back every field we changed */
          setFormTitle(originalTitle);
          setFormDescription(originalDescription);
          setFormDetails(originalDetails);


          // Open modal; navigation now happens only when the user clicks the button.
          setDuplicateModalState({
            open: true,
            formId: result.newFormId,
            message: result.message,
          });
        } else {
          console.error('[useDuplicateForm] ❌ Duplication failed:', result.message);
          setError(result.message || 'An error occurred while duplicating the form.');
        }
      } catch (err) {
        console.error('[useDuplicateForm] ❌ Unexpected error:', err);
        setError('Unexpected error during form duplication.');
      } finally {
        setLoading(false, 'duplicateForm');
      }
    },
 [
    currentUser,
    organizationId,
    hasPermission,
    initializeNewFormLocally,
    setFormId,
    setFormTitle,            // add to deps
    setFormDescription,
    setFormDetails, 
    formDetails,
    setLoading,
    formTitle,               // add to deps (used in originalTitle)
    formDescription          // add to deps (used in originalDescription)
  ]
  );

  /* -------------------------------------------------------------------- *
   * 2️⃣  User clicks “Go to Duplicated Form” in the modal                *
   * -------------------------------------------------------------------- */
  const goToDuplicatedForm = useCallback(() => {
    const { formId, open } = duplicateModalState;

    if (!open || !formId) {
      console.warn('[useDuplicateForm] goToDuplicatedForm called but modal is closed or formId missing.');
      return;
    }


  /* ⓵ Switch FormContext to the new ID immediately so route guards
     still watching /forms/:id don’t re‑evaluate with an old ID. */
  setFormId(formId);

    // Ensure FormContext has finished switching IDs
//    if (formId !== contextFormId || !formIdReady) {
//      console.warn('[useDuplicateForm] FormContext not ready yet; aborting navigation.');
//      return;
//    }

    /* 🧪 Final storage hydration check (same logic as before) */
    console.groupCollapsed(`[useDuplicateForm] 🧪 Final storage check before navigating: ${formId}`);

    const keysToCheck = [
      `form_${formId}_layoutConfig`,
      `form_${formId}_selectedTheme`,
      `form_${formId}_fieldsMetadata`,
      `form_${formId}_dropdownOptions`,
      `form_${formId}_dropdownDependencies`,
      `submissionSettings_${formId}`,
      `form_${formId}_colors`,
      `form_${formId}_fonts`,
      `form_${formId}_submitButtonStyle`,
      `form_${formId}_fieldVariant`,
    ];

    const missing = [];
    const present = [];

    keysToCheck.forEach((key) => {
      const value = safeGetItem(key);
      if (value) {
        present.push(key);
      } else {
        missing.push(key);
      }
    });

    if (present.length) {
      console.log('[useDuplicateForm] ✅ Keys found:', present);
    }
    if (missing.length) {
      console.warn('[useDuplicateForm] ⚠️ Keys missing:', missing);
    }

    console.groupEnd();

    if (missing.length > 0) {
      console.warn('[useDuplicateForm] ⏳ Navigation blocked: not all keys found in storage yet.');
      return;
    }

    console.log(`[useDuplicateForm] 🚀 Navigating to /form-builder/${formId}/layout`);
    navigate(`/form-builder/${formId}/layout`, {
      state: { duplicated: true },
    });

    // Close modal
    setDuplicateModalState({ open: false, formId: null, message: '' });
  }, [duplicateModalState, formIdReady, contextFormId, navigate]);

  /* -------------------------------------------------------------------- *
   * 3️⃣  Modal dismiss helper                                             *
   * -------------------------------------------------------------------- */
  const dismissDuplicateModal = () => {
    setDuplicateModalState({ open: false, formId: null, message: '' });
  };

  /* -------------------------------------------------------------------- *
   * Expose API                                                            *
   * -------------------------------------------------------------------- */
  return {
    handleDuplicateForm,   // call this to start duplication
    duplicateModalState,   // { open, formId, message }
    dismissDuplicateModal, // close the modal
    goToDuplicatedForm,    // call from DuplicateSuccessModal “Go to…” button
    error,                 // any duplication errors
  };
};

export default useDuplicateForm;
